<script setup lang="ts">
import { TuTable, TuTableRow } from "@tinyurldev/tu-ui-vue";

import type { TypedDnsRecord } from "@Resources/js/laravel.d.ts";
import useTranslation from "@Composables/useTranslation";

//
// Setup
const { __ } = useTranslation();

//
// Props
interface Props {
  records?: TypedDnsRecord[];
}

defineProps<Props>();
</script>

<template>
  <div class="dns-records">
    <h5 class="dns-records__title">
      {{ __("organization_domains_settings.dns_record_table.title") }}
    </h5>
    <p
      v-if="!records || records.length === 0"
      class="dns-records__empty"
    >
      {{ __("organization_domains_settings.dns_record_table.empty") }}
    </p>
    <TuTable
      v-else
      class="dns-records__table"
    >
      <TuTableRow
        v-for="(record, idx) in records"
        :key="idx"
      >
        <td colspan="4">
          <div class="dns-records__table-cell">
            <p class="dns-records__table-cell-title">
              {{ __("organization_domains_settings.dns_record_table.header_type") }}
            </p>
            <p class="dns-records__table-cell-content _type">
              {{ record.type }}
            </p>
          </div>
          <div class="dns-records__table-cell">
            <p class="dns-records__table-cell-title">
              {{ __("organization_domains_settings.dns_record_table.header_domain") }}
            </p>
            <p class="dns-records__table-cell-content _name">
              {{ record.name }}
            </p>
          </div>
          <div class="dns-records__table-cell">
            <p class="dns-records__table-cell-title">
              {{ __("organization_domains_settings.dns_record_table.header_value") }}
            </p>
            <p class="dns-records__table-cell-content _content">
              {{ record.content }}
            </p>
          </div>
          <div class="dns-records__table-cell">
            <p class="dns-records__table-cell-title">
              {{ __("organization_domains_settings.dns_record_table.header_ttl") }}
            </p>
            <p class="dns-records__table-cell-content _ttl">
              {{ record.ttl }}
            </p>
          </div>
        </td>
      </TuTableRow>
    </TuTable>
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;

.dns-records {
  display: flex;
  flex-flow: column;
  gap: spacing.$xxxs;

  &__title {
    margin: 0;
  }

  &__table-cell {
    display: grid;
    grid-template-columns: 1fr 1.5fr;
    gap: spacing.$xxxs;

    &:not(:last-child) {
      margin-bottom: spacing.$xxxs;
    }

    &:is(:first-child) {
      margin-top: spacing.$xxxs;
    }

    &:is(:last-child) {
      margin-bottom: spacing.$xxxs;
    }
  }

  &__table-cell-title {
    font-weight: typography.$font-weight-bold;
  }

  &__table-cell-content {
    font-weight: typography.$font-weight-medium;
  }

  &__table-cell-title,
  &__table-cell-content {
    margin: 0;

    font-size: typography.$base-font-size;
    line-height: typography.$base-line-height;
  }
}
</style>
